import { z } from 'zod';

import { ConversationStatusSchema } from '../../chat/conversation-status/conversation-status';
import { UuidSchema } from '../../common/id/id';

/**
 * What a guardian's approval did to the conversation (P24b2).
 *
 * ## Why the approve route answers with anything at all
 *
 * Because "I approved it" and "it is now open" are **not the same fact**, and a
 * client that assumed they were would be wrong exactly when an operator has the
 * setting on globally: both members are then gated, and the first wali to
 * approve leaves the conversation still waiting on the other one. A `204` would
 * make his app say "opened" and his ward's app disagree.
 *
 * So the payload is the conversation's resulting `status`, read from the row the
 * flip either moved or did not — never a boolean the service inferred. `opened`
 * is derived from it by the client rather than carried separately, for the
 * reason `isConversationWritable` exists: one fact, one spelling.
 *
 * It deliberately carries **nothing about the counterparty**. Composing that is
 * the list route's expensive job (`GuardianWardConversation`), it needs a locale
 * and a settings read, and a wali who has just pressed a button is looking at a
 * row he already has.
 */
export const GuardianApprovalOutcomeSchema = z
  .object({
    conversationId: UuidSchema,
    /**
     * Where the conversation stands **after** the approval. `active` when this
     * approval was the last one outstanding; `pending_guardian_approval` when
     * the other member's guardian has still not answered.
     *
     * Never `closed`: a conversation whose match ended refuses the approval
     * outright (`guardian_approval_not_claimable`) rather than reporting it.
     */
    status: ConversationStatusSchema,
  })
  .strict();

export type GuardianApprovalOutcome = z.infer<typeof GuardianApprovalOutcomeSchema>;
